import { Col, Form } from "react-bootstrap";
import styled, { css } from "styled-components";
import { InputBaseStyles } from "./basestyles.jsx";
import DatePicker from "react-datepicker";
import { forwardRef, useEffect } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { CustomFloatingLabel } from "./customfloatinglabel.jsx";
import { useTranslation } from "react-i18next";
import { registerLocale } from "react-datepicker";
import { useCommonAndCompanyEvents } from "../../hooks/useCommonAndCompanyEvents.js";
import { DateTime } from "luxon";
import fi from "date-fns/locale/fi";
import sv from "date-fns/locale/sv";
import enGB from "date-fns/locale/en-GB";
import nl from "date-fns/locale/nl";

registerLocale("fi", fi);
registerLocale("sv", sv);
registerLocale("en", enGB);
registerLocale("nl", nl);

const StyledDatePickerInput = styled(Form.Control)`
  ${InputBaseStyles};
  background-image: url("data:image/svg+xml,%3Csvg width='18' height='19' viewBox='0 0 18 19' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cg clip-path='url(%23clip0_428_10926)'%3E%3Cpath d='M12 10.25H9.75C9.3375 10.25 9 10.5875 9 11V13.25C9 13.6625 9.3375 14 9.75 14H12C12.4125 14 12.75 13.6625 12.75 13.25V11C12.75 10.5875 12.4125 10.25 12 10.25ZM12 2.75V3.5H6V2.75C6 2.3375 5.6625 2 5.25 2C4.8375 2 4.5 2.3375 4.5 2.75V3.5H3.75C2.9175 3.5 2.2575 4.175 2.2575 5L2.25 15.5C2.25 16.325 2.9175 17 3.75 17H14.25C15.075 17 15.75 16.325 15.75 15.5V5C15.75 4.175 15.075 3.5 14.25 3.5H13.5V2.75C13.5 2.3375 13.1625 2 12.75 2C12.3375 2 12 2.3375 12 2.75ZM13.5 15.5H4.5C4.0875 15.5 3.75 15.1625 3.75 14.75V7.25H14.25V14.75C14.25 15.1625 13.9125 15.5 13.5 15.5Z' fill='%23003A78'/%3E%3C/g%3E%3Cdefs%3E%3CclipPath id='clip0_428_10926'%3E%3Crect width='18' height='18' fill='white' transform='translate(0 0.5)'/%3E%3C/clipPath%3E%3C/defs%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: calc(100% - 10px) 50%;
  border-top: 0;
  border-left: 0;
  border-right: 0;
  height: 50px;
  margin-bottom: 0 !important;

  ${({ active }) =>
    active === "true" &&
    css`
      color: rgba(0, 58, 120, 1);
      background-color: rgba(230, 243, 254, 1);
    `}
`;

const DatePickerWrapper = styled.div`
  .react-datepicker-wrapper {
    width: 100%;
  }
  .react-datepicker__day.holiday {
    background-color: #c8c8c87d;
    color: rgba(25, 28, 212, 0.68);
    font-weight: bold;
    border: 1px solid #c8c8c8;
  }
`;

export const FormDatePicker = ({
  name,
  label,
  formGroupProps,
  rangePickerProps,
  required,
  disabled,
  customErrorMessage,
  additionalClassnames,
}) => {
  const { control } = useFormContext();
  const { t, i18n } = useTranslation();

  const start = DateTime.now().minus({ years: 1 }).startOf("year").toISODate();
  const end = DateTime.now().plus({ years: 2 }).endOf("year").toISODate();
  const { data: holidays = [] } = useCommonAndCompanyEvents({ start, end });

  const DatePickerInput = forwardRef(
    ({ value, onClick, active, onChange }, ref) => (
      <StyledDatePickerInput
        type="text"
        value={value}
        onClick={onClick}
        onChange={onChange}
        ref={ref}
        active={active.toString()}
      />
    ),
  );

  return (
    <Controller
      name={name}
      control={control}
      rules={{ required }}
      render={({
        field: { value, onChange, disabled: globalDisabled, ref },
        fieldState: { error, isDirty },
      }) => {
        const errorType = error?.type;

        useEffect(() => {
          if (value && typeof onChange === "function" && !isDirty) {
            onChange(new Date(value));
          }
        }, [value, onChange]);

        const helperText = () => {
          if (errorType === "required") return "The field is required";
          return customErrorMessage?.[errorType] ?? error?.message;
        };

        return (
          <>
            <Form.Group
              as={Col}
              xs={12}
              className={`DatePicker d-flex flex-column mb-2 ${additionalClassnames ?? ""}`}
              {...formGroupProps}
            >
              <DatePickerWrapper>
                <CustomFloatingLabel
                  active={isDirty.toString()}
                  label={`${label} ${required ? " *" : ""}`}
                >
                  <DatePicker
                    selected={value}
                    onChange={onChange}
                    customInput={<DatePickerInput active={isDirty} />}
                    dateFormat={
                      i18n.resolvedLanguage === "fi"
                        ? "d.M.yyyy"
                        : "yyyy-MM-dd"
                    }
                    placeholderText={label}
                    ref={ref}
                    disabled={disabled || globalDisabled}
                    required={required}
                    locale={i18n.resolvedLanguage}

                    highlightDates={holidays
                      .map(h => new Date(h.date))
                      .filter(d => !isNaN(d.getTime()))}

                    dayClassName={(date) => {
                      if (!date) return;
                      const iso = DateTime.fromJSDate(date).toISODate();

                      const match = holidays.find(h => h.date === iso);
                      return match ? "holiday" : undefined;
                    }}

                    renderDayContents={(day, date) => {
                      if (!date) return day;
                      const iso = DateTime.fromJSDate(date).toISODate();
                      const match = holidays.find(h => h.date === iso);
                      return (
                        <span title={match ? t(match.descKey) : ""}>
                          {day}
                        </span>
                      );
                    }}

                    {...rangePickerProps}
                  />
                </CustomFloatingLabel>
              </DatePickerWrapper>

              {error && (
                <Form.Control.Feedback type="invalid" className="d-block mb-1">
                  {helperText()}
                </Form.Control.Feedback>
              )}
            </Form.Group>
          </>
        );
      }}
    />
  );
};
